import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, X, BookOpen, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getNutritionBasics, startBasicsQuiz, submitBasicsQuiz } from "@/lib/api";

// Coach course → Nutrition basics: six short lessons, each with a fresh 10-question quiz marked
// instantly. Green means right, red means wrong. The reply units open once all six are passed.

const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

function ProteinTable({ table, foods }) {
  const [open, setOpen] = useState(false);
  if (!table && !foods) return null;
  const rows = foods ? Object.entries(foods).map(([food, f]) => [food, f]) : Object.entries(table).map(([food, g]) => [food, { protein_g: g }]);
  return (
    <div className="text-sm">
      <button type="button" onClick={() => setOpen(!open)} className="text-emerald-300 hover:text-emerald-200">
        {open ? "Hide" : "Show"} the food table</button>
      {open && (
        <div className="mt-2 rounded-lg border border-[#262626] p-3 max-h-72 overflow-y-auto">
          {foods && <p className="text-xs text-zinc-500 mb-2">Values from FatSecret India. Tap a food to see its FatSecret page.</p>}
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-0.5">
            <span className="text-xs text-zinc-500">Food</span><span className="text-xs text-zinc-500 text-right">Protein</span><span className="text-xs text-zinc-500 text-right">kcal</span>
            {rows.sort((a, b) => b[1].protein_g - a[1].protein_g).map(([food, f]) => (
              <div key={food} className="contents">
                <span className="text-zinc-300">{f.source ? <a href={f.source} target="_blank" rel="noreferrer" className="hover:underline">{food}</a> : food}
                  {f.serving ? <span className="text-zinc-500"> · {f.serving}</span> : null}</span>
                <span className="tabular-nums text-zinc-200 text-right">{f.protein_g} g</span>
                <span className="tabular-nums text-zinc-400 text-right">{f.kcal ?? ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Quiz({ lesson, onDone, onBack, table, foods }) {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const start = useCallback(() => {
    setResult(null); setAnswers({});
    startBasicsQuiz(lesson.n).then(setQuiz).catch((e) => err(e, "Couldn't start the quiz"));
  }, [lesson.n]);
  useEffect(() => { start(); }, [start]);
  if (!quiz) return <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />;
  const submit = async () => {
    setBusy(true);
    try { const r = await submitBasicsQuiz(quiz.id, answers); setResult(r); onDone(); } catch (e) { err(e, "Couldn't mark it"); }
    setBusy(false);
  };
  const answered = quiz.questions.filter((q) => String(answers[q.id] ?? "").trim() !== "").length;
  const byId = Object.fromEntries((result?.results || []).map((r) => [r.id, r]));
  return (
    <section className="card-surface p-5 sm:p-6 space-y-4" data-testid="basics-quiz">
      <div>
        <div className="eyebrow">Lesson {lesson.n} quiz · {quiz.pass} out of 10 to pass</div>
        <h2 className="font-display text-xl font-bold mt-1">{lesson.title}</h2>
      </div>
      {lesson.n >= 3 && <ProteinTable table={table} foods={foods} />}
      {result && (
        <div className={`rounded-lg border p-4 ${result.passed ? "border-emerald-500/40 bg-emerald-500/10" : "border-rose-500/40 bg-rose-500/10"}`}>
          <div className="font-display text-3xl font-bold tabular-nums">{result.score}/{result.out_of}</div>
          <div className={result.passed ? "text-emerald-300" : "text-rose-300"}>{result.passed ? "Passed" : `Not yet. You need ${quiz.pass}. Read the wrong ones below, then try a new set.`}</div>
        </div>
      )}
      <ol className="space-y-4 list-decimal pl-5">
        {quiz.questions.map((q) => {
          const r = byId[q.id];
          return (
            <li key={q.id} className="space-y-2">
              <div className="text-zinc-100">{q.prompt}</div>
              {q.type === "choice" ? (
                <div className="grid gap-1.5">
                  {q.options.map((o) => (
                    <label key={o} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${
                      r ? (o === r.answer ? "border-emerald-500/50 bg-emerald-500/10" : answers[q.id] === o ? "border-rose-500/50 bg-rose-500/10" : "border-[#262626]")
                        : answers[q.id] === o ? "border-white bg-white/5" : "border-[#262626] hover:border-zinc-500"}`}>
                      <input type="radio" name={q.id} value={o} disabled={!!r} checked={answers[q.id] === o}
                        onChange={() => setAnswers({ ...answers, [q.id]: o })} />{o}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input value={answers[q.id] ?? ""} disabled={!!r} inputMode="decimal" className="w-32 bg-[#0a0a0a] border-[#262626]"
                    onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} />
                  <span className="text-sm text-zinc-400">{q.unit}</span>
                </div>
              )}
              {r && (
                <p className={`text-sm flex gap-1.5 ${r.correct ? "text-emerald-300" : "text-rose-300"}`}>
                  {r.correct ? <Check className="h-4 w-4 mt-0.5 shrink-0" /> : <X className="h-4 w-4 mt-0.5 shrink-0" />}
                  <span>{r.correct ? "Right. " : `The answer is ${r.answer}${r.unit ? ` ${r.unit}` : ""}. `}<span className="text-zinc-300">{r.explain}</span></span>
                </p>
              )}
            </li>
          );
        })}
      </ol>
      <div className="flex flex-wrap gap-2">
        {!result && <Button onClick={submit} disabled={busy || answered < quiz.questions.length} className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-6">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Mark my answers ({answered}/{quiz.questions.length})</Button>}
        {result && <Button onClick={start} className="rounded-full">New set of questions</Button>}
        <Button variant="outline" onClick={onBack} className="rounded-full">All lessons</Button>
      </div>
    </section>
  );
}

export default function NutritionBasics({ onUnitsOpen }) {
  const [home, setHome] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [quizzing, setQuizzing] = useState(false);
  const opened = useRef(onUnitsOpen);
  opened.current = onUnitsOpen;
  const load = useCallback(() => getNutritionBasics().then((h) => { setHome(h); if (h.units_open) opened.current?.(); })
    .catch((e) => err(e, "Couldn't load the basics")), []);
  useEffect(() => { load(); }, [load]);
  if (!home) return <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />;

  if (lesson && quizzing) return <Quiz lesson={lesson} table={home.protein_table} foods={home.food_table} onDone={load} onBack={() => { setQuizzing(false); setLesson(null); }} />;
  if (lesson) {
    return (
      <section className="card-surface p-5 sm:p-6 space-y-4" data-testid="basics-lesson">
        <div className="eyebrow">Nutrition basics · Lesson {lesson.n} of {home.lessons.length}</div>
        <h2 className="font-display text-xl font-bold">{lesson.title}</h2>
        <ul className="space-y-2.5 max-w-2xl">
          {lesson.points.map((p, i) => <li key={i} className="flex gap-2 text-zinc-200"><span className="text-emerald-400 font-semibold">{i + 1}.</span><span>{p}</span></li>)}
        </ul>
        {lesson.n >= 3 && <ProteinTable table={home.protein_table} foods={home.food_table} />}
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setQuizzing(true)} className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-6">Take the quiz</Button>
          <Button variant="outline" onClick={() => setLesson(null)} className="rounded-full">All lessons</Button>
        </div>
      </section>
    );
  }
  const passed = home.lessons.filter((l) => home.progress[l.n]?.passed).length;
  return (
    <div className="space-y-4" data-testid="nutrition-basics">
      <section className="card-surface p-5 sm:p-6">
        <div className="eyebrow">Start here</div>
        <h3 className="font-display text-lg font-bold mt-1">Nutrition basics</h3>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">Six short lessons on how LIFTR does nutrition: calories, protein, plates, portions and eating out. Read each one, then take a 10-question quiz. Pass with {home.pass} out of 10. The reply units open once all six are passed.</p>
        <p className="text-sm mt-2"><span className="tabular-nums font-semibold">{passed}/{home.lessons.length}</span> passed{home.all_passed ? <span className="text-emerald-300"> · the reply units are open</span> : null}</p>
      </section>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {home.lessons.map((l) => {
          const p = home.progress[l.n] || {};
          return (
            <button key={l.n} type="button" onClick={() => setLesson(l)} data-testid={`basics-lesson-${l.n}`}
              className={`card-surface p-4 text-left space-y-2 hover:border-zinc-500 ${p.passed ? "border-emerald-500/40" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="eyebrow">Lesson {l.n}</span>
                {p.passed ? <span className="flex items-center gap-1 text-xs font-semibold text-emerald-300"><Award className="h-4 w-4" />Passed</span>
                  : <BookOpen className="h-4 w-4 text-zinc-500" />}
              </div>
              <div className="font-semibold">{l.title}</div>
              <div className="text-xs text-zinc-500">{p.attempts ? `Best ${p.best}/10 · ${p.attempts} ${p.attempts === 1 ? "try" : "tries"}` : "Not started"}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
